/**
 * SnippetsPanel — the clips + influences library, one panel two tabs
 * (docs/design/SNIPPETS-SPEC.md "Library panel"; docs/design/STEAL-SPEC.md
 * "UI" — "one library, two kinds of holdings" rather than a second sidebar
 * item). A page, not a modal (Fieldbook §6, matching WhyThisPanel): slides
 * in from the right, never covers the canvas.
 *
 * Clips tab: a mini shape-strip preview (the function->shape/wash mapping
 * from ChordShape, no interaction inside it), an editable name and caption,
 * a play button that hears it exactly as captured (original key/voices,
 * uncommitted), "add to page", and delete. Dragging a card onto the canvas
 * timeline inserts it at the drop beat in the CURRENT key/mode — see
 * DroppableCanvas's native HTML5 drop handler, which reads the
 * `application/x-neume-snippet` payload this panel writes on drag start.
 * Native DnD (not @dnd-kit) because this panel lives outside
 * DroppableCanvas's DndContext tree.
 *
 * Influences tab: each card shows trait rows as horizontal weight bars
 * (wash-tonic fill on a rule track) with evidence in handwriting beneath.
 * "voice it like {name}" runs planFromInfluence and hands the result to the
 * shared review-queue-store — the SAME queue ShiftBar renders (see
 * ReviewQueue.tsx), not a second copy.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { useModalStore } from '@/store/modal-store';
import { useSnippetsStore } from '@/store/snippets-store';
import { useInfluencesStore } from '@/store/influences-store';
import { generateShapePath, getFunctionWashVar } from '@/components/Canvas/ChordShape';
import { SNIPPET_DRAG_MIME } from '@/utils/snippet-insert';
import { MOVES } from '@/lib/moves';
import type { Chord, Snippet, SnippetChord } from '@/types';
import type { Influence } from '@/types/influence';
import styles from './SnippetsPanel.module.css';

type LibraryTab = 'clips' | 'influences';

interface SnippetsPanelProps {
  /** Hear the clip exactly as captured — original key/voices, uncommitted. */
  onPlaySnippet: (chords: Chord[]) => void;
  /** Non-drag insertion: append the clip after the last chord on the canvas. */
  onAppendSnippet: (snippetId: string) => void;
  /** planFromInfluence -> the shared review queue. */
  onVoiceItLike: (influence: Influence) => void;
}

/** Snippet -> playback Chords, using the voices stored as-captured (no revoice). */
function snippetToPlaybackChords(snippet: Snippet): Chord[] {
  const now = new Date().toISOString();
  return snippet.chords.map((sc, index) => ({
    id: `snippet-preview-${snippet.id}-${index}`,
    scaleDegree: sc.scaleDegree,
    quality: sc.quality,
    extensions: sc.extensions,
    key: snippet.key,
    mode: snippet.mode,
    isChromatic: sc.isChromatic ?? false,
    chromaticType: sc.chromaticType,
    voices: sc.voices ?? { soprano: 'C4', alto: 'G3', tenor: 'E3', bass: 'C3' },
    startBeat: sc.startBeat,
    duration: sc.duration,
    position: { x: 0, y: 0 },
    size: 80,
    selected: false,
    playing: false,
    source: 'library',
    createdAt: now,
  }));
}

const SHAPE_SIZE = 22;

const SnippetShapeStrip: React.FC<{ chords: SnippetChord[] }> = ({ chords }) => (
  <div className={styles.shapeStrip} aria-hidden="true">
    {chords.map((c, index) => (
      <svg key={index} width={SHAPE_SIZE} height={SHAPE_SIZE} viewBox={`0 0 ${SHAPE_SIZE} ${SHAPE_SIZE}`}>
        <path
          d={generateShapePath(c.scaleDegree, SHAPE_SIZE)}
          fill={getFunctionWashVar(c)}
          fillOpacity={0.4}
          stroke="var(--ink)"
          strokeWidth={1}
        />
      </svg>
    ))}
  </div>
);

interface SnippetCardProps {
  snippet: Snippet;
  onPlay: () => void;
  onAppend: () => void;
}

const SnippetCard: React.FC<SnippetCardProps> = ({ snippet, onPlay, onAppend }) => {
  const renameSnippet = useSnippetsStore((s) => s.renameSnippet);
  const updateCaption = useSnippetsStore((s) => s.updateCaption);
  const deleteSnippet = useSnippetsStore((s) => s.deleteSnippet);

  const [name, setName] = useState(snippet.name);
  const [caption, setCaption] = useState(snippet.caption);

  useEffect(() => setName(snippet.name), [snippet.name]);
  useEffect(() => setCaption(snippet.caption), [snippet.caption]);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.dataTransfer.setData(SNIPPET_DRAG_MIME, snippet.id);
      e.dataTransfer.effectAllowed = 'copy';
    },
    [snippet.id]
  );

  return (
    <div className={styles.card} draggable onDragStart={handleDragStart}>
      <SnippetShapeStrip chords={snippet.chords} />

      <input
        className={styles.nameInput}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (name.trim() && name !== snippet.name) renameSnippet(snippet.id, name.trim());
        }}
        aria-label="Clip name"
      />

      <input
        className={styles.captionInput}
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        onBlur={() => {
          if (caption !== snippet.caption) updateCaption(snippet.id, caption);
        }}
        aria-label="Clip caption"
      />

      <div className={styles.cardActions}>
        <button type="button" className={styles.link} onClick={onPlay}>
          hear it
        </button>
        <button type="button" className={styles.link} onClick={onAppend}>
          add to page
        </button>
        <button type="button" className={styles.link} onClick={() => deleteSnippet(snippet.id)}>
          delete
        </button>
      </div>
    </div>
  );
};

function moveDisplayName(moveId: string): string {
  return MOVES.find((m) => m.id === moveId)?.name ?? moveId;
}

interface InfluenceCardProps {
  influence: Influence;
  onVoiceItLike: () => void;
}

const InfluenceCard: React.FC<InfluenceCardProps> = ({ influence, onVoiceItLike }) => {
  const renameInfluence = useInfluencesStore((s) => s.renameInfluence);
  const deleteInfluence = useInfluencesStore((s) => s.deleteInfluence);

  const [name, setName] = useState(influence.name);
  useEffect(() => setName(influence.name), [influence.name]);

  const rankedTraits = [...influence.traits].sort((a, b) => b.weight - a.weight);
  const pieceCount = influence.pieces.length;

  return (
    <div className={styles.card}>
      <input
        className={styles.nameInput}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => {
          if (name.trim() && name !== influence.name) renameInfluence(influence.id, name.trim());
        }}
        aria-label="Influence name"
      />

      <p className={styles.pieceCount}>
        {pieceCount} {pieceCount === 1 ? 'piece' : 'pieces'} learned
      </p>

      {rankedTraits.length > 0 && (
        <div className={styles.traitRows}>
          {rankedTraits.map((trait) => (
            <div key={trait.moveId} className={styles.traitRow}>
              <div className={styles.traitHead}>
                <span className={styles.traitName}>{moveDisplayName(trait.moveId)}</span>
              </div>
              <div className={styles.traitBarTrack}>
                <div
                  className={styles.traitBarFill}
                  style={{ width: `${Math.round(trait.weight * 100)}%` }}
                />
              </div>
              <p className={styles.traitEvidence}>{trait.evidence}</p>
            </div>
          ))}
        </div>
      )}

      <div className={styles.cardActions}>
        <button type="button" className={styles.link} onClick={onVoiceItLike}>
          voice it like {influence.name}
        </button>
        <button type="button" className={styles.link} onClick={() => deleteInfluence(influence.id)}>
          delete
        </button>
      </div>
    </div>
  );
};

export const SnippetsPanel: React.FC<SnippetsPanelProps> = ({ onPlaySnippet, onAppendSnippet, onVoiceItLike }) => {
  const isOpen = useModalStore((s) => s.isOpen('snippets'));
  const closePanel = useCallback(() => useModalStore.getState().closeModal('snippets'), []);
  const [activeTab, setActiveTab] = useState<LibraryTab>('clips');

  const snippets = useSnippetsStore((s) => s.snippets);
  const loadSnippets = useSnippetsStore((s) => s.loadSnippets);
  const influences = useInfluencesStore((s) => s.influences);
  const loadInfluences = useInfluencesStore((s) => s.loadInfluences);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (isOpen) {
      loadSnippets();
      loadInfluences();
    }
  }, [isOpen, loadSnippets, loadInfluences]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePanel]);

  const handlePlay = useCallback(
    (snippet: Snippet) => {
      onPlaySnippet(snippetToPlaybackChords(snippet));
    },
    [onPlaySnippet]
  );

  const content = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* No dimming/click-blocking overlay here, unlike WhyThisPanel or
              CritiquePanel: this panel's core interactions (dragging a card
              onto the canvas, "voice it like" populating the ReviewQueue
              dock in the same corner) both need the rest of the app to stay
              interactive while the panel is open. Esc or the close button
              dismiss it. */}
          <motion.div
            className={styles.panel}
            initial={prefersReducedMotion ? { opacity: 0 } : { x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { x: '100%', opacity: 0 }}
            transition={{ duration: prefersReducedMotion ? 0.12 : 0.3, ease: [0.4, 0.0, 0.2, 1] }}
            role="complementary"
            aria-labelledby="snippets-panel-title"
          >
            <div className={styles.header}>
              <h2 id="snippets-panel-title" className={styles.title}>
                {activeTab === 'clips' ? 'clips' : 'influences'}
              </h2>
              <button className={styles.closeButton} onClick={closePanel} aria-label="Close panel" title="Close (Esc)">
                ✕
              </button>
            </div>

            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'clips'}
                className={`${styles.tab} ${activeTab === 'clips' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('clips')}
              >
                clips
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'influences'}
                className={`${styles.tab} ${activeTab === 'influences' ? styles.tabActive : ''}`}
                onClick={() => setActiveTab('influences')}
              >
                influences
              </button>
            </div>

            <div className={styles.content}>
              {activeTab === 'clips' ? (
                snippets.length === 0 ? (
                  <p className={styles.emptyNote}>
                    select a passage on the canvas and clip it — it lands here, ready to drag into anything.
                  </p>
                ) : (
                  snippets.map((snippet) => (
                    <SnippetCard
                      key={snippet.id}
                      snippet={snippet}
                      onPlay={() => handlePlay(snippet)}
                      onAppend={() => onAppendSnippet(snippet.id)}
                    />
                  ))
                )
              ) : influences.length === 0 ? (
                <p className={styles.emptyNote}>
                  analyze a piece, then "learn this piece" to start distilling a composer's habits here.
                </p>
              ) : (
                influences.map((influence) => (
                  <InfluenceCard
                    key={influence.id}
                    influence={influence}
                    onVoiceItLike={() => onVoiceItLike(influence)}
                  />
                ))
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};
